import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { ArrowRight, Zap, Shield, Award, Bot, Check } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import FeaturedBrandsAndBestsellers from '@/components/FeaturedBrandsAndBestsellers';

const DEFAULT_BANNER = 'https://customer-assets.emergentagent.com/job_impact-golf-shops/artifacts/2yfem0xb_HomeBanner.png';

// Helper function to darken/lighten a hex color
function adjustColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

export default function HomePage() {
  const { tenantPath, branding } = useTenant();
  const bannerImage = branding?.banner_image_url || DEFAULT_BANNER;
  
  // Branding colors with defaults
  const primaryColor = branding?.primary_color || '#4A6741';
  const accentColor = branding?.accent_color || '#D4A55A';
  
  // Darker shade of primary for gradients/hovers (darken by ~15%)
  const primaryDark = adjustColor(primaryColor, -15);
  const accentDark = adjustColor(accentColor, -10);
  
  // Custom text from branding or defaults
  const heroTitle = branding?.hero_title || "PRECISION\nENGINEERED";
  const heroSubtitle = branding?.hero_subtitle || "Custom golf equipment built to your exact specifications. Experience the difference precision makes on every shot.";
  const heroCtaText = branding?.hero_cta_text || "Shop Custom";
  const categoriesTitle = branding?.categories_title || "Explore Categories";
  const categoriesSubtitle = branding?.categories_subtitle || "Find the perfect equipment for your game";
  const featuresTitle = branding?.features_title || "Why Choose Us";
  const featuresSubtitle = branding?.features_subtitle || "Golfshop.fit difference";
  const ctaTitle = branding?.cta_title || "Ready to Elevate Your Game?";
  const ctaSubtitle = branding?.cta_subtitle || "Browse our collection of custom shafts and equipment, or contact us for a personalized fitting consultation.";
  
  const categories = [
    {
      name: 'Custom Shafts',
      image: 'https://customer-assets.emergentagent.com/job_golf-ecommerce/artifacts/hwv8ku0f_Shafts.jpg',
      path: '/category/shafts',
      span: 'md:col-span-6',
    },
    {
      name: 'Drivers',
      image: 'https://customer-assets.emergentagent.com/job_golf-ecommerce/artifacts/b00fscpt_Drivers.jpg',
      path: '/category/drivers',
      span: 'md:col-span-6',
    },
    {
      name: 'Irons',
      image: 'https://customer-assets.emergentagent.com/job_golf-ecommerce/artifacts/ftb1c1vf_irons.webp',
      path: '/category/irons',
      span: 'md:col-span-4',
    },
    {
      name: 'Wedges',
      image: 'https://customer-assets.emergentagent.com/job_golf-ecommerce/artifacts/8g718xhv_wedges.jpg',
      path: '/category/wedges',
      span: 'md:col-span-4',
    },
  ];
  
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      {/* Hero Section with Links Golf Background */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('${bannerImage}')`,
            backgroundPosition: 'center 60%'
          }}
        />
        {/* Gradient overlay for text readability */}
        <div 
          className="absolute inset-0"
          style={{ background: `linear-gradient(to right, ${primaryDark}cc, ${primaryDark}66, transparent)` }}
        />
        <div className="grain-texture absolute inset-0" />
        
        <div className="relative z-10 text-left px-6 md:px-12 lg:px-24 max-w-7xl w-full">
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-none uppercase text-white mb-6"
            style={{ fontFamily: 'Chivo, sans-serif', fontWeight: 900 }}
            data-testid="hero-title"
          >
            {heroTitle.split('\n').map((line, i) => (
              <span key={i}>
                {i === 1 ? <span style={{ color: accentColor }}>{line}</span> : line}
                {i < heroTitle.split('\n').length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl" data-testid="hero-subtitle">
            {heroSubtitle}
          </p>
          <div className="flex flex-wrap gap-4 mb-6">
            <Link 
              to={tenantPath("/category/shafts")} 
              className="inline-flex items-center space-x-2 h-14 px-10 text-white font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = primaryDark}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
              data-testid="shop-custom-button"
            >
              <span>{heroCtaText}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link 
              to={tenantPath("/fitting-wizard")} 
              className="inline-flex items-center space-x-2 h-14 px-10 font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg"
              style={{ backgroundColor: accentColor, color: primaryDark }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = accentDark}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = accentColor}
              data-testid="hero-ai-fitter-button"
            >
              <Bot className="h-5 w-5" />
              <span>AI Club Fitter</span>
            </Link>
          </div>
          <div className="flex items-center space-x-3 text-white/80">
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: accentColor }}></span>
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: accentColor }}></span>
              </span>
              <span className="text-sm font-medium">Try our AI-powered fitting wizard — get personalized shaft recommendations in minutes</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Categories Grid */}
      <FeaturedBrandsAndBestsellers />
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FAFAF8]">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Chivo, sans-serif', color: primaryDark }}
              data-testid="categories-title"
            >
              {categoriesTitle}
            </h2>
            <p className="text-lg" style={{ color: `${primaryColor}99` }} data-testid="categories-subtitle">{categoriesSubtitle}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
            {categories.map((category) => (
              <Link
                key={category.path}
                to={tenantPath(category.path)}
                className={`${category.span} group relative h-80 bg-[#F5F3EF] overflow-hidden rounded-xl border border-[#E8E4DC] hover:border-[#C9B896] transition-all shadow-sm hover:shadow-md`}
                data-testid={`category-card-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url('${category.image}')` }}
                />
                <div 
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(to top, ${primaryDark}e6, ${primaryDark}4d, transparent)` }}
                />
                
                <div className="absolute inset-0 flex items-end p-8">
                  <div>
                    <h3 
                      className="text-3xl font-bold text-white mb-2"
                      style={{ fontFamily: 'Chivo, sans-serif' }}
                    >
                      {category.name}
                    </h3>
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: accentColor }}>
                      <span className="text-sm font-bold uppercase tracking-wider">Explore</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI Club Fitting Wizard Section */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{ background: `linear-gradient(to bottom right, ${primaryDark}, ${primaryColor}, ${adjustColor(primaryColor, 10)})` }}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" style={{ backgroundColor: accentColor }}></div>
        </div>
        
        <div className="container mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div 
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-bold mb-6"
                style={{ backgroundColor: `${accentColor}33`, color: accentColor }}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: accentColor }}></span>
                  <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: accentColor }}></span>
                </span>
                <span>AI-POWERED</span>
              </div>
              
              <h2 
                className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Meet Your Personal
                <br />
                <span style={{ color: accentColor }}>Club Fitting Wizard</span>
              </h2>
              
              <p className="text-white/80 text-lg mb-8 max-w-lg">
                Not sure which shaft is right for you? Our AI-powered fitting wizard analyzes your swing characteristics and recommends the perfect shaft configuration in minutes.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}33` }}>
                    <Check className="h-4 w-4" style={{ color: accentColor }} />
                  </div>
                  <span className="text-white/90">Personalized flex & weight recommendations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}33` }}>
                    <Check className="h-4 w-4" style={{ color: accentColor }} />
                  </div>
                  <span className="text-white/90">Driver, Fairway Wood & Iron shaft fitting</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}33` }}>
                    <Check className="h-4 w-4" style={{ color: accentColor }} />
                  </div>
                  <span className="text-white/90">Get 3 configuration options tailored to your goals</span>
                </div>
              </div>
              
              <Link 
                to={tenantPath("/fitting-wizard")} 
                className="inline-flex items-center space-x-3 h-14 px-8 font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg hover:shadow-xl"
                style={{ backgroundColor: accentColor, color: primaryDark }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = accentDark}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = accentColor}
                data-testid="homepage-fitting-wizard-btn"
              >
                <Bot className="h-5 w-5" />
                <span>Start Free Fitting</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            
            {/* Right - Chat Preview */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-2xl">
                <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-white/10">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                    <Bot className="h-5 w-5" style={{ color: primaryDark }} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">Club Fitting Wizard</h4>
                    <p className="text-white/60 text-xs">AI-powered recommendations</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-xl rounded-bl-sm p-4 max-w-[85%]">
                    <p className="text-white/90 text-sm">What's your typical driver carry distance?</p>
                  </div>
                  <div className="rounded-xl rounded-br-sm p-4 max-w-[85%] ml-auto" style={{ backgroundColor: `${accentColor}33` }}>
                    <p className="text-white text-sm">About 250 yards</p>
                  </div>
                  <div className="bg-white/10 rounded-xl rounded-bl-sm p-4 max-w-[85%]">
                    <p className="text-white/90 text-sm">Based on your profile, I recommend:</p>
                    <div className="mt-2 text-sm font-medium" style={{ color: accentColor }}>
                      ✓ Stiff Flex (S)<br/>
                      ✓ 60-65g Weight<br/>
                      ✓ Mid Launch Profile
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-24" style={{ backgroundColor: primaryDark }}>
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="features-title"
            >
              {featuresTitle}
            </h2>
            <p className="text-lg" style={{ color: `${accentColor}cc` }} data-testid="features-subtitle">{featuresSubtitle}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-white/5 rounded-xl border border-white/10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg mb-6" style={{ backgroundColor: primaryColor }}>
                <Zap className="h-8 w-8" style={{ color: accentColor }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>Custom Built</h3>
              <p className="text-white/70">Every club is built to your exact specifications for optimal performance</p>
            </div>
            
            <div className="text-center p-8 bg-white/5 rounded-xl border border-white/10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg mb-6" style={{ backgroundColor: primaryColor }}>
                <Shield className="h-8 w-8" style={{ color: accentColor }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>Premium Materials</h3>
              <p className="text-white/70">Only the finest components from trusted manufacturers worldwide</p>
            </div>
            
            <div className="text-center p-8 bg-white/5 rounded-xl border border-white/10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg mb-6" style={{ backgroundColor: primaryColor }}>
                <Award className="h-8 w-8" style={{ color: accentColor }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>Expert Craftsmanship</h3>
              <p className="text-white/70">Built by certified club builders with decades of experience</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 bg-[#F5F3EF]">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Chivo, sans-serif', color: primaryDark }}
            data-testid="cta-title"
          >
            {ctaTitle}
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: `${primaryColor}99` }} data-testid="cta-subtitle">
            {ctaSubtitle}
          </p>
          <Link 
            to={tenantPath("/category/shafts")} 
            className="inline-flex items-center space-x-2 h-14 px-10 text-white font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg"
            style={{ backgroundColor: primaryColor }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = primaryDark}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
          >
            <span>Browse Collection</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
